import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import { Container, Card, CardBody, Row, Col, Button, Table } from 'reactstrap'
import CurrencyFormat from 'react-currency-format'
import Moment from 'react-moment'
import toastr from 'toastr'
import 'toastr/build/toastr.min.css'

import Breadcrumbs from '../../components/Global/Breadcrumbs'
import ApiService from '../../data/services/ApiService'
import {
    getStatusBadge,
    getTipoComprobanteInfo,
    buildEgresoEmisor,
    buildEgresoReceptor,
    buildEgresoTotales,
    buildEgresoTimbre,
} from '../../helpers/egresoDetailHelper'

// ── Styles ───────────────────────────────────────────────────────────────────

const SECTION_TITLE = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: 'var(--color-text-tertiary, #888)',
    marginBottom: 8,
    marginTop: 16,
    borderBottom: '0.5px solid var(--color-border-tertiary, #e9ecef)',
    paddingBottom: 4,
}

const FIELD_LABEL = {
    fontSize: 11,
    color: 'var(--color-text-secondary, #888)',
    marginBottom: 1,
}

const FIELD_VALUE = {
    fontSize: 13,
    color: 'var(--color-text-primary, #222)',
    marginBottom: 8,
    wordBreak: 'break-word',
}

// ── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonLine = ({ width = '100%', height = 14, style = {} }) => (
    <div
        style={{
            width,
            height,
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.4s infinite',
            borderRadius: 4,
            marginBottom: 8,
            ...style,
        }}
    />
)

const LoadingSkeleton = () => (
    <Card>
        <CardBody>
            <style>{`
                @keyframes shimmer {
                    0%   { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
            `}</style>
            <SkeletonLine width="40%" height={22} />
            <SkeletonLine width="25%" height={16} style={{ marginBottom: 24 }} />
            <SkeletonLine width="60%" />
            <SkeletonLine width="50%" />
            <SkeletonLine width="70%" />
            <SkeletonLine width="55%" />
        </CardBody>
    </Card>
)

// ── Field helper ─────────────────────────────────────────────────────────────

const Field = ({ label, value }) => (
    <div>
        <div style={FIELD_LABEL}>{label}</div>
        <div style={FIELD_VALUE}>{value || '—'}</div>
    </div>
)

// ── Main component ────────────────────────────────────────────────────────────

const EgresoDetail = () => {
    const { id } = useParams()
    const history = useHistory()

    const [loading, setLoading] = useState(true)
    const [error, setError]     = useState(null)
    const [egreso, setEgreso]   = useState(null)
    const [emisor, setEmisor]   = useState(null)
    const [receptor, setReceptor] = useState(null)
    const [totales, setTotales] = useState(null)
    const [timbre, setTimbre]   = useState(null)
    const [complemento, setComplemento] = useState(null)

    const _load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await ApiService.getEgreso(id)
            const { egreso: e } = response.data.data

            setEgreso(e)
            setEmisor(buildEgresoEmisor(e))
            setReceptor(buildEgresoReceptor(e))
            setTotales(buildEgresoTotales(e))
            setTimbre(buildEgresoTimbre(e))
            setComplemento(e.complemento || null)
        } catch (err) {
            const msg = err.response?.data?.message || 'No pude cargar el egreso.'
            setError(msg)
            toastr.error(msg)
        } finally {
            setLoading(false)
        }
    }, [id])

    useEffect(() => {
        _load()
    }, [_load])

    const _downloadXml = () => {
        const blob = new Blob([egreso.xml], { type: 'application/xml' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${egreso.uuid || egreso._id}.xml`
        document.body.appendChild(link)
        link.click()
        link.parentNode.removeChild(link)
        window.URL.revokeObjectURL(url)
    }

    const _downloadZip = async (egresoId, uuid) => {
        try {
            const response = await ApiService.descargarEgreso(egresoId)
            const url = URL.createObjectURL(response.data)
            const a = document.createElement('a')
            a.href = url
            a.download = `${uuid || egresoId}.zip`
            a.click()
            URL.revokeObjectURL(url)
        } catch (err) {
            toastr.error(err.response?.data?.message || 'No pude descargar el archivo.')
        }
    }

    if (loading) {
        return (
            <Container fluid>
                <Breadcrumbs title="Egresos" breadcrumbItem="Detalle de Egreso" parentLink="/egresos" />
                <LoadingSkeleton />
            </Container>
        )
    }

    if (error || !egreso) {
        return (
            <Container fluid>
                <Breadcrumbs title="Egresos" breadcrumbItem="Detalle de Egreso" parentLink="/egresos" />
                <Card>
                    <CardBody>
                        <p style={{ color: '#f46a6a', marginBottom: 16 }}>
                            {error || 'Egreso no encontrado.'}
                        </p>
                        <Button color="secondary" size="sm" onClick={() => history.goBack()}>
                            ← Volver
                        </Button>
                    </CardBody>
                </Card>
            </Container>
        )
    }

    const badge    = getStatusBadge(egreso.status)
    const tipoInfo = getTipoComprobanteInfo(egreso.tipoDeComprobante)

    return (
        <Container fluid>
            <Breadcrumbs
                title="Egresos"
                breadcrumbItem="Detalle de Egreso"
                parentLink="/egresos"
            />

            <Card>
                <CardBody>
                    {/* ── Header ──────────────────────────────────────────── */}
                    <Row className="align-items-center mb-3">
                        <Col sm={8}>
                            <h4 style={{ margin: 0, fontWeight: 600 }}>
                                {tipoInfo.label}
                                {tipoInfo.badgeLabel && (
                                    <span
                                        className="badge ml-2"
                                        style={{ fontSize: '0.78rem', fontWeight: 600, padding: '3px 11px' }}
                                    >
                                        {tipoInfo.badgeLabel}
                                    </span>
                                )}
                            </h4>
                            {egreso.fechaGasto && (
                                <div style={{ fontSize: 12, color: 'var(--color-text-secondary, #888)', marginTop: 4 }}>
                                    Fecha del gasto:{' '}
                                    <Moment format="DD/MM/YYYY">{egreso.fechaGasto}</Moment>
                                </div>
                            )}
                            <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                {egreso.importadoDelSAT === true && (
                                    <span
                                        style={{
                                            background: '#e9ecef',
                                            color: '#495057',
                                            borderRadius: 4,
                                            padding: '2px 8px',
                                            fontSize: 11,
                                            fontWeight: 600,
                                        }}
                                    >
                                        Importada del SAT
                                    </span>
                                )}
                                {egreso.categoria && (
                                    <span
                                        style={{
                                            background: '#f8f9fa',
                                            color: '#495057',
                                            borderRadius: 4,
                                            border: '1px solid #dee2e6',
                                            padding: '2px 8px',
                                            fontSize: 11,
                                        }}
                                    >
                                        {egreso.categoria}
                                    </span>
                                )}
                                <span
                                    style={{
                                        background: egreso.deducible ? '#d4edda' : '#f8d7da',
                                        color:      egreso.deducible ? '#155724' : '#721c24',
                                        borderRadius: 4,
                                        padding: '2px 8px',
                                        fontSize: 11,
                                        fontWeight: 600,
                                    }}
                                >
                                    {egreso.deducible ? 'Deducible' : 'No deducible'}
                                </span>
                            </div>
                        </Col>
                        <Col sm={4} className="text-right">
                            <span
                                style={{
                                    background: badge.color,
                                    color: '#fff',
                                    borderRadius: 4,
                                    padding: '4px 12px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                }}
                            >
                                {badge.label}
                            </span>
                        </Col>
                    </Row>

                    {/* ── Emisor ─────────────────────────────────────────── */}
                    <div style={SECTION_TITLE}>Emisor</div>
                    <Row>
                        <Col sm={6}>
                            <Field label="Nombre / Razón Social" value={emisor?.nombre} />
                        </Col>
                        <Col sm={3}>
                            <Field label="RFC" value={emisor?.rfc} />
                        </Col>
                        <Col sm={3}>
                            <Field label="Régimen Fiscal" value={emisor?.regimenFiscal} />
                        </Col>
                    </Row>

                    {/* ── Receptor ───────────────────────────────────────── */}
                    <div style={SECTION_TITLE}>Receptor</div>
                    <Row>
                        <Col sm={6}>
                            <Field label="Nombre / Razón Social" value={receptor?.nombre} />
                        </Col>
                        <Col sm={3}>
                            <Field label="RFC" value={receptor?.rfc} />
                        </Col>
                    </Row>

                    {/* ── Importes ───────────────────────────────────────── */}
                    <div style={SECTION_TITLE}>Importes</div>
                    {totales && (
                        <Row>
                            <Col sm={{ size: 4, offset: 8 }}>
                                <Table size="sm" borderless style={{ fontSize: 13 }}>
                                    <tbody>
                                        {totales.moneda !== 'MXN' && (
                                            <tr>
                                                <td>Moneda</td>
                                                <td className="text-right">
                                                    {totales.moneda} (TC: {totales.tipoCambio})
                                                </td>
                                            </tr>
                                        )}
                                        <tr>
                                            <td>Subtotal</td>
                                            <td className="text-right">
                                                <CurrencyFormat value={totales.subtotal} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                                            </td>
                                        </tr>
                                        {totales.descuento > 0 && (
                                            <tr>
                                                <td>Descuento</td>
                                                <td className="text-right">
                                                    <CurrencyFormat value={totales.descuento} displayType="text" thousandSeparator prefix="-$" decimalScale={2} fixedDecimalScale />
                                                </td>
                                            </tr>
                                        )}
                                        {totales.totalTrasladados > 0 && (
                                            <tr>
                                                <td>Impuestos trasladados</td>
                                                <td className="text-right">
                                                    <CurrencyFormat value={totales.totalTrasladados} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                                                </td>
                                            </tr>
                                        )}
                                        {totales.totalRetenidos > 0 && (
                                            <tr>
                                                <td>Retenciones</td>
                                                <td className="text-right">
                                                    <CurrencyFormat value={totales.totalRetenidos} displayType="text" thousandSeparator prefix="-$" decimalScale={2} fixedDecimalScale />
                                                </td>
                                            </tr>
                                        )}
                                        <tr style={{ borderTop: '1px solid #dee2e6', fontWeight: 600 }}>
                                            <td>Total</td>
                                            <td className="text-right">
                                                <CurrencyFormat value={totales.total} displayType="text" thousandSeparator prefix="$" decimalScale={2} fixedDecimalScale />
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                    )}

                    {/* ── Timbre ─────────────────────────────────────────── */}
                    {timbre && (
                        <>
                            <div style={SECTION_TITLE}>Timbre Fiscal Digital</div>
                            <Row>
                                <Col sm={9}>
                                    <Field label="UUID" value={timbre.uuid?.toUpperCase()} />
                                </Col>
                                <Col sm={3}>
                                    {timbre.fechaTimbrado && (
                                        <Field
                                            label="Fecha de Timbrado"
                                            value={<Moment format="DD/MM/YYYY HH:mm">{timbre.fechaTimbrado}</Moment>}
                                        />
                                    )}
                                </Col>
                            </Row>
                        </>
                    )}

                    {/* ── Complemento de Pago Recibido ───────────────────── */}
                    {complemento && (
                        <>
                            <div style={SECTION_TITLE}>Complemento de Pago Recibido</div>
                            <Row className="align-items-center">
                                <Col sm={6}>
                                    <Field label="UUID del Complemento" value={complemento.uuid?.toUpperCase()} />
                                    {complemento.fechaGasto && (
                                        <Field
                                            label="Fecha"
                                            value={<Moment format="DD/MM/YYYY">{complemento.fechaGasto}</Moment>}
                                        />
                                    )}
                                    {complemento.total > 0 && (
                                        <Field
                                            label="Monto pagado"
                                            value={
                                                <CurrencyFormat
                                                    value={complemento.total}
                                                    displayType="text"
                                                    thousandSeparator
                                                    prefix="$"
                                                    decimalScale={2}
                                                    fixedDecimalScale
                                                />
                                            }
                                        />
                                    )}
                                </Col>
                                <Col sm={6} className="text-right">
                                    <Button
                                        color="outline-primary"
                                        size="sm"
                                        className="mr-2"
                                        onClick={() => history.push(`/egresos/${complemento._id}/detalle`)}
                                    >
                                        Ver complemento
                                    </Button>
                                    <Button
                                        color="outline-secondary"
                                        size="sm"
                                        onClick={() => _downloadZip(complemento._id, complemento.uuid)}
                                    >
                                        Descargar complemento (ZIP)
                                    </Button>
                                </Col>
                            </Row>
                        </>
                    )}

                    {/* ── Actions ────────────────────────────────────────── */}
                    <div style={SECTION_TITLE}>Acciones</div>
                    <Row className="align-items-center">
                        <Col sm={12} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {egreso.xml && (
                                <Button color="outline-secondary" size="sm" onClick={_downloadXml}>
                                    <i className="bx bxs-file-blank mr-1"></i> Descargar XML
                                </Button>
                            )}
                            {egreso.xml && (
                                <Button
                                    color="outline-success"
                                    size="sm"
                                    onClick={() => _downloadZip(egreso._id, egreso.uuid)}
                                >
                                    <i className="bx bxs-file-archive mr-1"></i> Descargar ZIP (XML + PDF)
                                </Button>
                            )}
                            <Button color="secondary" size="sm" onClick={() => history.goBack()}>
                                ← Volver
                            </Button>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        </Container>
    )
}

export default EgresoDetail
